"use client"

import {
  API_ORIGIN,
  EvaluationAnalysisView,
  PublicShareLayout,
  usePublicShareRouteParam,
} from '@yeshuman/sdk'
import type { EvaluationAnalysisPayload, UnifiedCandidate } from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { SearchCandidatesResponse, CandidateMatch } from '../../hooks/use-briefs'
import type { JobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'
import type { JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import type { ApplicantCard } from '../../hooks/use-opportunity-applicants'
import { SmartAnalysisResultsShell } from '../search/smart-analysis-results-shell'

const EXPIRED = 'This report link has expired.'
const REPORT_PATH = '/share/analysis/:token'
const PERSON_PATH = '/share/analysis/:token/people/:profileId'

function isFrozenAnalysis(value: unknown): value is EvaluationAnalysisPayload {
  if (!value || typeof value !== 'object') return false
  const payload = value as EvaluationAnalysisPayload
  return Boolean(payload.scoring_trace && payload.findings && payload.narrative)
}

function findSharedPerson(snapshot: SharedReport['snapshot'], profileId: string) {
  const applicant = snapshot.applicants?.find((item) => item.profile.id === profileId)
  if (applicant) {
    return {
      name: applicant.profile.name,
      analysis: isFrozenAnalysis(applicant.analysis) ? applicant.analysis : null,
    }
  }
  const prospect = snapshot.prospect_matches?.find((item) => item.profile_id === profileId)
  if (prospect) {
    return {
      name: prospect.name,
      analysis: isFrozenAnalysis(prospect.analysis) ? prospect.analysis : null,
    }
  }
  const match = snapshot.results?.top_matches?.find((item) => item.profile_id === profileId)
  if (match) {
    return {
      name: match.candidate_name,
      analysis: isFrozenAnalysis((match as { analysis?: unknown }).analysis)
        ? ((match as { analysis?: unknown }).analysis as EvaluationAnalysisPayload)
        : null,
    }
  }
  return null
}

type SharedReport = {
  title: string
  note: string
  redacted: boolean
  kind: 'job' | 'brief'
  expires_at: string
  snapshot: {
    narrative?: string
    narrative_at?: string | null
    applicants?: ApplicantCard[]
    prospect_matches?: JobProspectMatch[]
    coverage?: JobAnalysisCoverage | null
    results?: SearchCandidatesResponse | null
  }
}

function rankedFromApplicants(applicants: ApplicantCard[]): UnifiedCandidate[] {
  return applicants
    .filter((applicant) => typeof applicant.match_score === 'number')
    .slice()
    .sort((left, right) => (right.match_score ?? 0) - (left.match_score ?? 0))
    .map((applicant, index) => {
      const score = applicant.match_score ?? 0
      const full: CandidateMatch = {
        rank: index + 1,
        profile_id: applicant.profile.id,
        candidate_name: applicant.profile.name,
        final_score: score,
        structured_score: score,
        semantic_score: score,
        evaluation_id: applicant.evaluation_id ?? null,
        found_by: ['application'],
        was_llm_judged: false,
      }
      return {
        profile_id: applicant.profile.id,
        name: applicant.profile.name,
        score,
        strategy: 'application',
        strategies: ['application'],
        state: 'analyzed' as const,
        rank: index + 1,
        full,
      }
    })
}

function matchesToCandidates(matches: CandidateMatch[]): UnifiedCandidate[] {
  return matches.map((match) => ({
    profile_id: match.profile_id,
    name: match.candidate_name,
    score: match.final_score,
    state: 'analyzed' as const,
    rank: match.rank,
    full: match,
  }))
}

export function SharedAnalysisReportPage() {
  const profileId = usePublicShareRouteParam(PERSON_PATH, 'profileId')
  const token = usePublicShareRouteParam(profileId ? PERSON_PATH : REPORT_PATH, 'token')
  const navigate = useNavigate()
  const [report, setReport] = useState<SharedReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        setLoading(false)
        setError(EXPIRED)
        return
      }
      setLoading(true)
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/applications/reports/shared/${encodeURIComponent(token)}/`,
        )
        if (!response.ok) {
          setError(EXPIRED)
          setReport(null)
          return
        }
        const body = (await response.json()) as SharedReport
        if (!cancelled) {
          setReport(body)
          setError(null)
        }
      } catch {
        if (!cancelled) setError(EXPIRED)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token])

  const snapshot = report?.snapshot
  const applicants = snapshot?.applicants ?? []
  const ranked = rankedFromApplicants(applicants)
  const briefMatches = snapshot?.results?.top_matches ?? []
  const briefCandidates = matchesToCandidates(briefMatches)
  const noop = () => undefined
  const openPerson = (nextProfileId: string) => {
    navigate(
      `/share/analysis/${encodeURIComponent(token)}/people/${encodeURIComponent(nextProfileId)}`,
    )
  }
  const person = profileId && snapshot ? findSharedPerson(snapshot, profileId) : null

  return (
    <PublicShareLayout>
      <div className="space-y-6 px-4 py-8">
        {loading ? <p className="text-sm text-muted-foreground">Loading the report…</p> : null}
        {error ? <p className="text-sm text-foreground">{error}</p> : null}
        {profileId && report && snapshot ? (
          <>
            <a
              href={`/share/analysis/${encodeURIComponent(token)}`}
              className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              Back to the report
            </a>
            {person?.analysis ? (
              <>
                <header className="space-y-2">
                  <h1 className="text-2xl font-semibold tracking-tight">{person.name}</h1>
                  {report.redacted ? (
                    <p className="text-sm text-muted-foreground">
                      Names and contact details in this report are stand-ins.
                    </p>
                  ) : null}
                </header>
                <EvaluationAnalysisView frozenPayload={person.analysis} subjectLabel={person.name} />
              </>
            ) : (
              <p className="text-sm text-foreground">
                This report does not include an analysis for this person.
              </p>
            )}
          </>
        ) : null}
        {!profileId && report && snapshot ? (
          <>
            <header className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">{report.title}</h1>
              {report.note ? <p className="text-sm text-foreground">{report.note}</p> : null}
              {report.redacted ? (
                <p className="text-sm text-muted-foreground">
                  Names and contact details in this report are stand-ins.
                </p>
              ) : null}
            </header>
            {report.kind === 'job' ? (
              <SmartAnalysisResultsShell
                direction="candidates"
                layoutVariant="job-report"
                liveItems={ranked}
                results={
                  ranked.length > 0
                    ? {
                        session_id: `shared-${token}`,
                        total_candidates_evaluated: ranked.length,
                        llm_judged_count: 0,
                        top_matches: ranked.map((candidate) => candidate.full!).filter(Boolean),
                        search_summary: snapshot.narrative || undefined,
                      }
                    : null
                }
                stages={[]}
                isSearching={false}
                searchError={null}
                introJobScoped
                introEmptyActiveApplicants={applicants.length === 0}
                analysisLastUpdatedAt={snapshot.narrative_at}
                jobReportLiveComplete={ranked.length > 0}
                searchSummary={snapshot.narrative || null}
                applicantProfileById={Object.fromEntries(
                  applicants.map((applicant) => [applicant.profile.id, applicant.profile]),
                )}
                jobReport={{
                  applicants,
                  prospectMatches: snapshot.prospect_matches ?? [],
                  coverage: snapshot.coverage ?? null,
                  onReviewPipeline: noop,
                  onReviewInvite: noop,
                  onViewInPipeline: noop,
                  readOnly: true,
                }}
                onOpenPerson={openPerson}
              />
            ) : (
              <SmartAnalysisResultsShell
                direction="candidates"
                liveItems={briefCandidates}
                results={snapshot.results ?? null}
                stages={[]}
                isSearching={false}
                searchError={null}
                introJobScoped
                searchSummary={snapshot.narrative || snapshot.results?.search_summary || null}
                onOpenPerson={openPerson}
                openOnlyWithAnalysis
              />
            )}
          </>
        ) : null}
      </div>
    </PublicShareLayout>
  )
}
